// Beam primitive — a cylinder stretched from `position` (origin) to
// `def.target`, fading out over `def.durationSec`. Used for arrows,
// lich drain beams, lightning, etc.

import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { BeamDef } from './types.js';

interface BeamProps {
  readonly def: BeamDef;
  readonly position: readonly [number, number, number];
  readonly targetOverride?: readonly [number, number, number];
}

export function Beam({ def, position, targetOverride }: BeamProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const matRef = useRef<THREE.MeshBasicMaterial>(null);
  const startTimeRef = useRef(performance.now());
  const target = targetOverride ?? def.target;

  const { midpoint, length, orientation } = useMemo(() => {
    const from = new THREE.Vector3(...position);
    const to = new THREE.Vector3(...target);
    const diff = to.clone().sub(from);
    const len = diff.length() || 0.001;
    const mid = from.clone().add(to).multiplyScalar(0.5);
    // Orient cylinder (default +Y) along diff.
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, diff.clone().normalize());
    const euler = new THREE.Euler().setFromQuaternion(quat);
    return {
      midpoint: [mid.x, mid.y, mid.z] as readonly [number, number, number],
      length: len,
      orientation: [euler.x, euler.y, euler.z] as readonly [number, number, number],
    };
  }, [position, target]);

  const geometry = useMemo(
    () => new THREE.CylinderGeometry(def.width, def.width, length, 8),
    [def.width, length],
  );

  useFrame(() => {
    const mat = matRef.current;
    if (!mat) return;
    const elapsed = (performance.now() - startTimeRef.current) / 1000;
    const t = Math.min(elapsed / def.durationSec, 1);
    // Fade out with a slight ramp-up at t=0..0.1.
    const opacity = t < 0.1 ? t * 10 : 1 - (t - 0.1) / 0.9;
    mat.opacity = Math.max(0, opacity);
  });

  return (
    <mesh ref={meshRef} position={midpoint} rotation={orientation} geometry={geometry}>
      <meshBasicMaterial
        ref={matRef}
        color={def.color}
        transparent
        opacity={1}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}
